import React from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import CustomAdminModal from "../../../components/common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { Cloud } from "../../../components/svgs/CustomCoursework";

export enum BulkUploadModalState {
  CLOSED = "CLOSED",
  UPLOAD_CONFIRMATION = "UPLOAD_CONFIRMATION",
  UPLOAD_ERROR = "UPLOAD_ERROR",
}

export interface CourseUploadResult {
  courseName: string;
  success: boolean;
  error?: string;
}

interface BulkUploadCourseModalProps {
  visible: boolean;
  isProcessing: boolean;
  processingStatus: string;
  selectedFile: File | null;
  uploadResults: CourseUploadResult[];
  isMedium: boolean;
  onClose: () => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDownloadTemplate: () => void;
  onUpload: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

// This component is used to parse an excel file and generate courses based on the row data in the excel file.
// We do not upload the file to server.
const BulkUploadCourseModal: React.FC<BulkUploadCourseModalProps> = ({
  visible,
  isProcessing,
  processingStatus,
  selectedFile,
  uploadResults,
  isMedium,
  onClose,
  onFileChange,
  onDownloadTemplate,
  onUpload,
  fileInputRef,
}) => {
  const renderFileUpload = () => (
    <View style={styles.fileUpload}>
      <Cloud />
      <CustomSpacing type="vertical" size="s" />
      <CustomText text="Drag & drop or browse" size="m" style={{ color: "#344054" }} />
      <CustomText text="Excel files only (.xlsx, .xls)" size="xs" style={{ color: "#5177FE" }} />
      <CustomSpacing type="vertical" size="m" />
      <input type="file" accept=".xlsx,.xls" onChange={onFileChange} style={{ display: "none" }} ref={fileInputRef} />
      <CustomButton text="Browse Files" onPress={() => fileInputRef.current?.click()} styleType="primary" size="m" />
    </View>
  );

  const renderResults = () => {
    // Count successes and errors
    const successCount = uploadResults.filter((result) => result.success).length;
    const errorCount = uploadResults.filter((result) => !result.success).length;

    return (
      <View style={styles.resultsContainer}>
        <View style={styles.resultsSummary}>
          <CustomText text="Upload Results" size="m" weight="bold" />
          <View style={styles.resultsCounts}>
            {successCount > 0 && (
              <CustomText
                text={`${successCount} successful`}
                size="xs"
                style={{ color: "#10B981", marginRight: 8 }}
                useTranslationText={false}
              />
            )}
            {errorCount > 0 && (
              <CustomText
                text={`${errorCount} failed`}
                size="xs"
                style={{ color: "#EF4444" }}
                useTranslationText={false}
              />
            )}
          </View>
        </View>
        <CustomSpacing type="vertical" size="s" />
        <View style={styles.resultsList}>
          {uploadResults.map((result, index) => (
            <View key={index} style={[styles.resultItem, result.success ? styles.successItem : styles.errorItem]}>
              <View style={styles.resultHeader}>
                <CustomText text={result.courseName} size="s" weight="semiBold" useTranslationText={false} />
                <CustomText
                  text={result.success ? "Success" : "Failed"}
                  size="xs"
                  style={{
                    color: result.success ? "#10B981" : "#EF4444",
                    fontWeight: "bold",
                  }}
                  useTranslationText={false}
                />
              </View>

              {!result.success && result.error && (
                <View style={styles.errorDetails}>
                  <CustomText text={result.error} size="xs" style={{ color: "#EF4444" }} useTranslationText={false} />
                </View>
              )}
            </View>
          ))}
        </View>

        {errorCount > 0 && (
          <View style={styles.errorHelp}>
            <CustomText
              text="Please fix the errors and try again. Download the template for reference."
              size="xs"
              style={{ color: "#6B7280" }}
            />
          </View>
        )}
      </View>
    );
  };

  return (
    <CustomAdminModal
      visible={visible}
      onRequestClose={onClose}
      title="Bulk Upload Courses"
      ariaLabel="Bulk upload courses modal"
      style={{ width: isMedium ? "50%" : "90%", maxHeight: "90vh" }}
      onShow={() => {}}>
      <ScrollView style={styles.modalScrollView} showsVerticalScrollIndicator={true} tabIndex={0}>
        <View style={styles.container}>
          <CustomText
            text="Upload an Excel file to create multiple courses at once. Each row in the file represents a course."
            size="s"
            style={styles.description}
          />
          <CustomSpacing type="vertical" size="m" />

          <View style={styles.templateSection}>
            <CustomText text="Need a template?" size="s" weight="semiBold" />
            <CustomButton
              text="Download Template"
              onPress={onDownloadTemplate}
              styleType="secondary"
              size="s"
              style={styles.downloadButton}
            />
          </View>

          <CustomSpacing type="vertical" size="m" />

          {renderFileUpload()}

          {selectedFile && (
            <View style={styles.selectedFileContainer}>
              <CustomText
                text={`Selected file: ${selectedFile.name}`}
                size="s"
                style={{ color: "#10B981" }}
                useTranslationText={false}
              />
            </View>
          )}

          {isProcessing && (
            <View style={styles.processingContainer}>
              <CustomText text={processingStatus} size="s" style={{ color: "#3E68FE" }} useTranslationText={false} />
            </View>
          )}

          {uploadResults.length > 0 && renderResults()}

          <View style={styles.buttonContainer}>
            <CustomButton
              text="Cancel"
              onPress={onClose}
              styleType="secondary"
              size="m"
              style={styles.cancelButton}
              textStyle={styles.cancelButtonText}
              disabled={isProcessing}
            />
            <CustomButton
              text="Upload and Process"
              onPress={onUpload}
              styleType="primary"
              size="m"
              style={!selectedFile || isProcessing ? { backgroundColor: "#ccc" } : styles.uploadButton}
              disabled={!selectedFile || isProcessing}
            />
          </View>
        </View>
      </ScrollView>
    </CustomAdminModal>
  );
};

const styles = StyleSheet.create({
  modalScrollView: {
    flex: 1,
    width: "100%",
  },
  container: {
    padding: 24,
  },
  description: {
    color: "#6B7280",
  },
  templateSection: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  downloadButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
  },
  fileUpload: {
    borderWidth: 1,
    borderColor: "#6788FE",
    borderRadius: 8,
    borderStyle: "dashed",
    padding: 24,
    alignItems: "center",
    backgroundColor: "#F2F5FF",
  },
  selectedFileContainer: {
    marginTop: 16,
  },
  processingContainer: {
    marginTop: 16,
    padding: 12,
    backgroundColor: "#EFF6FF",
    borderRadius: 8,
  },
  resultsContainer: {
    marginTop: 24,
  },
  resultsSummary: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  resultsCounts: {
    flexDirection: "row",
    alignItems: "center",
  },
  resultsList: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    maxHeight: 300,
    overflow: "scroll",
  },
  resultItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  resultHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  errorDetails: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#FECACA",
  },
  successItem: {
    backgroundColor: "#F0FDF4",
  },
  errorItem: {
    backgroundColor: "#FEF2F2",
  },
  errorHelp: {
    marginTop: 12,
    padding: 8,
    backgroundColor: "#F9FAFB",
    borderRadius: 4,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    marginTop: 24,
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  uploadButton: {
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  cancelButtonText: {
    color: "#4B5563",
  },
});

export default BulkUploadCourseModal;
